import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Users, Plus, Pencil, Trash2, KeyRound, Unlock, Power, Search } from "lucide-react";
import { toast } from "sonner";
import { apiCall, copyText } from "@/components/admin/people/peopleUtils";

const EMPTY = { parent_name: "", parent_email: "", phone: "", status: "active" };

export default function AdminParents() {
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null); // {mode:'create'|'edit', data}
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiCall("/admin/auth/subscribers/parents");
      setParents(d.parents || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = parents.filter((p) => {
    const q = search.toLowerCase();
    return !q || (p.parent_name || "").toLowerCase().includes(q) || (p.parent_email || "").toLowerCase().includes(q);
  });

  const openCreate = () => { setForm(EMPTY); setModal({ mode: "create" }); };
  const openEdit = (p) => {
    setForm({ parent_name: p.parent_name || "", parent_email: p.parent_email || "", phone: p.phone || "", status: p.status || "active" });
    setModal({ mode: "edit", data: p });
  };

  const save = async () => {
    if (!form.parent_name.trim() || !form.parent_email.trim()) { toast.error("Name and email are required"); return; }
    setSaving(true);
    try {
      if (modal.mode === "create") {
        await apiCall("/admin/auth/subscribers/parents", { method: "POST", body: form });
        toast.success("Parent created");
      } else {
        await apiCall(`/admin/auth/subscribers/parents/${modal.data.id}`, { method: "PUT", body: form });
        toast.success("Parent updated");
      }
      setModal(null); load();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const act = async (p, action) => {
    try {
      if (action === "delete") {
        if (!window.confirm(`Delete parent ${p.parent_email}? This cannot be undone.`)) return;
        await apiCall(`/admin/auth/subscribers/parents/${p.id}`, { method: "DELETE" });
        toast.success("Parent deleted");
      } else if (action === "unlock") {
        await apiCall(`/admin/users/parent/${p.id}/unlock`, { method: "POST" });
        toast.success("Account unlocked");
      } else if (action === "reset") {
        const d = await apiCall(`/admin/users/parent/${p.id}/reset-password`, { method: "POST" });
        if (d.temp_password) copyText(d.temp_password, "Temp password");
        else toast.success("Reset link/OTP sent");
      }
      load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-6" data-testid="admin-parents-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Users className="h-6 w-6 text-indigo-600" /> Parents</h1>
          <p className="text-sm text-slate-500">Manage all parent accounts (B2C & B2B)</p>
        </div>
        <Button onClick={openCreate} className="bg-indigo-600 hover:bg-indigo-700" data-testid="parent-create-btn"><Plus className="h-4 w-4 mr-2" /> Add Parent</Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or email" className="pl-10" data-testid="parents-search" />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-left">
            <tr><th className="p-4">Name</th><th className="p-4">Email</th><th className="p-4">Children</th><th className="p-4">Status</th><th className="p-4 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {loading ? (<tr><td colSpan={5} className="p-8 text-center text-slate-400">Loading…</td></tr>)
              : filtered.length === 0 ? (<tr><td colSpan={5} className="p-8 text-center text-slate-400">No parents found</td></tr>)
              : filtered.map((p) => (
                <tr key={p.id} className="border-t border-slate-100" data-testid={`parent-row-${p.id}`}>
                  <td className="p-4 font-medium text-slate-800">{p.parent_name || "—"}</td>
                  <td className="p-4 text-slate-600">{p.parent_email}</td>
                  <td className="p-4 text-slate-600">{(p.children || []).length}</td>
                  <td className="p-4"><Badge className={p.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>{p.status || "pending"}</Badge></td>
                  <td className="p-4">
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="ghost" title="Edit" onClick={() => openEdit(p)} data-testid={`parent-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" title="Reset password" onClick={() => act(p, "reset")} data-testid={`parent-reset-${p.id}`}><KeyRound className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" title="Unlock" onClick={() => act(p, "unlock")} data-testid={`parent-unlock-${p.id}`}><Unlock className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" title="Delete" className="text-red-600 hover:bg-red-50" onClick={() => act(p, "delete")} data-testid={`parent-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!modal} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent data-testid="parent-modal">
          <DialogHeader><DialogTitle>{modal?.mode === "create" ? "Add Parent" : "Edit Parent"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Parent name" value={form.parent_name} onChange={(e) => setForm({ ...form, parent_name: e.target.value })} data-testid="parent-name-input" />
            <Input placeholder="Parent email" value={form.parent_email} onChange={(e) => setForm({ ...form, parent_email: e.target.value })} disabled={modal?.mode === "edit"} data-testid="parent-email-input" />
            <Input placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="parent-phone-input" />
            <select className="w-full border border-slate-200 rounded-md p-2 text-sm" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="parent-status-select">
              <option value="active">Active</option><option value="pending">Pending</option><option value="suspended">Suspended</option>
            </select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="parent-save-btn">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
